import { useMemo } from 'react';
import { Box, ListItemButton } from '@mui/material';
import { ScheduleTask, TaskListCategory } from '../types';
import SortedToPlanList from 'components/roster-portal/activity-planning/components/ToPlanSidebar/SortedToPlanList';
import { RouteContextMenu } from 'components/roster-portal/activity-planning/components';
import dayjs from 'dayjs';
import { PRIMARY_LIGHT, TEXT } from 'constants/colors';
import TaskListAllDone from './TaskListAllDone';
import { scrollToRoute } from 'components/roster-portal/activity-planning/utils';
import { ContextMenuOptions } from 'components/roster-portal/activity-planning/components/ContextMenu/types';
import { EventResponse } from 'generated/index';
import { PlanningEntity } from 'components/roster-portal/activity-planning/types';

type TaskListContentProps = {
  category: TaskListCategory;
  scheduleToAssign: ScheduleTask[];
  activeEntityContainingSelection: string | null;
  onChangeActiveEntity: () => void;
  onChangeSelection: () => void;
  hasActivityPlanningEditAccess: boolean;
  onChangeMousePosition: (position: { x: number; y: number }) => void;
  planEventsPerEntity: {
    planningEntityId: string;
    planEvents: EventResponse[];
    planningEntity: PlanningEntity;
  }[];
  onRouteClick?: (date: Date, routeId?: string) => void;
  isTimeline?: boolean;
};

export const TaskListContent = (props: TaskListContentProps) => {
  const {
    category,
    scheduleToAssign,
    activeEntityContainingSelection,
    onChangeActiveEntity,
    onChangeSelection,
    hasActivityPlanningEditAccess,
    onChangeMousePosition,
    planEventsPerEntity,
    onRouteClick,
    isTimeline = false
  } = props;

  const hideOptions = [
    ContextMenuOptions.CONNECT_ROUTE,
    ContextMenuOptions.SHOW_ROUTE_ON_MAP,
    ContextMenuOptions.EDIT_ROUTE_START,
    ContextMenuOptions.CHANGE_TO_CAR,
    ContextMenuOptions.CHANGE_TO_BICYCLE
  ];

  if (category === TaskListCategory.TO_PLAN_APPOINTMENTS) {
    const hasEvents = planEventsPerEntity.some(
      ({ planEvents }) => planEvents.length > 0
    );
    const multiplePlanningEntitiesSelected = planEventsPerEntity.length > 1;

    return hasEvents ? (
      <>
        {planEventsPerEntity.map(
          ({ planningEntityId, planEvents, planningEntity }) =>
            planEvents.length > 0 && (
              <SortedToPlanList
                key={planningEntityId}
                activeEntityContainingSelection={
                  activeEntityContainingSelection
                }
                planningEntityId={planningEntityId}
                planningEntityType={planningEntity.type}
                readOnly={!hasActivityPlanningEditAccess}
                events={planEvents ?? []}
                onChangeActiveEntity={onChangeActiveEntity}
                onChangeSelection={onChangeSelection}
                onChangeMousePosition={onChangeMousePosition}
                multiplePlanningEntitiesSelected={
                  multiplePlanningEntitiesSelected
                }
              />
            )
        )}
      </>
    ) : (
      <TaskListAllDone />
    );
  }

  if (category === TaskListCategory.SCHEDULE_TO_ASSIGN) {
    if (scheduleToAssign.length > 0) {
      const planningEntityIdCounts = useMemo(() => {
        return scheduleToAssign.reduce((counts, task) => {
          const id = task?.data?.planningEntityId ?? '';
          counts[id] = (counts[id] || 0) + 1;
          return counts;
        }, {});
      }, [scheduleToAssign]);

      const taskListItems = useMemo(() => {
        return scheduleToAssign.map((task, index) => {
          const id = task?.data?.planningEntityId ?? '';
          const showDayName = planningEntityIdCounts[id] > 1;

          return (
            <Box key={index + (task?.data?.planningEntityId ?? '')}>
              <ListItemButton
                onClick={() => {
                  const startTime = task?.data?.start;
                  const routeId = task?.data?.planningEntityId; // Use the route's ID here
                  if (isTimeline && onRouteClick && startTime) {
                    onRouteClick(new Date(startTime), routeId); // Pass routeId to scrollToDay
                  } else {
                    scrollToRoute(
                      task?.data?.appointmentGrouping?.entityId ?? ''
                    );
                  }
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: 0.5,
                  paddingY: 1,
                  bgcolor: PRIMARY_LIGHT,
                  alignItems: 'center',
                  py: 1,
                  pl: 2,
                  pr: 0,
                  borderRadius: 1.5,
                  boxShadow: '0px 2px 1px #CBEFED',
                  '&:hover': {
                    boxShadow: '0px 2px 1px #e8ebeb'
                  }
                }}
              >
                <Box display="flex" gap={2} alignItems="center">
                  <Box
                    fontSize={12}
                    color={TEXT}
                    display="flex"
                    flexDirection="column"
                  >
                    {showDayName && (
                      <Box textTransform="capitalize">
                        {dayjs(task?.data?.start).format('dddd')}
                      </Box>
                    )}
                    <Box>
                      {dayjs(task?.data?.start).format('HH:mm')} -{' '}
                      {dayjs(task?.data?.end).format('HH:mm')}
                    </Box>
                  </Box>
                  <Box fontSize={14} color={TEXT}>
                    {task?.data?.name}
                  </Box>
                </Box>
                <RouteContextMenu
                  appointmentGroupingEntityId={
                    task?.data?.appointmentGrouping?.entityId ?? ''
                  }
                  appointmentGroupingStart={task?.data?.start ?? ''}
                  appointmentGroupingName={task?.data?.name}
                  events={task?.data?.events}
                  employeeId={null}
                  isAssignableToEmployee={
                    task?.data?.isAssignableToEmployee ?? false
                  }
                  scheduleId={task?.data?.planningEntityId ?? ''}
                  optionsToHide={hideOptions}
                />
              </ListItemButton>
            </Box>
          );
        });
      }, [scheduleToAssign, planningEntityIdCounts]);

      return (
        <Box display="flex" flexDirection="column" gap={0.75} paddingBottom={1}>
          {taskListItems}
        </Box>
      );
    } else {
      return <TaskListAllDone />;
    }
  }

  return null;
};
